import {tva} from "@gluestack-ui/nativewind-utils/tva";
import type {ReactNode} from "react";
import type React from "react";
import {memo, useMemo} from "react";
import {View} from "react-native";

import {Center} from "../ui/center";
import {Image} from "../ui/image";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

import calculateDimension, {type AspectRatio} from "./calculateDimension";

type Props = {
    imgAspectRatio?: AspectRatio;
    primaryText: string;
    secondaryText?: ReactNode;
    children?: ReactNode;
    className?: string;
} & StrictUnion<
    | {
          SVGComponent?: React.FC<React.SVGProps<any>>;
          imgWidth?: number;
          imgHeight?: number;
      }
    | StrictUnion<
          | {
                isNative?: false;
                imgUrl?: string;
                imgWidth?: number;
                imgHeight?: number;
            }
          | {
                isNative: true;
                imgUrl?: number;
                imgWidth?: number;
                imgHeight?: number;
            }
      >
>;

const style = tva({
    base: "grow shrink items-center justify-center min-h-80"
});

const EmptyState: React.FC<Props> = ({
    primaryText,
    secondaryText,
    children,
    imgUrl,
    SVGComponent,
    imgWidth,
    imgHeight,
    imgAspectRatio,
    className
}) => {
    const source = useMemo(() => (typeof imgUrl === "number" ? imgUrl : {uri: imgUrl}), [imgUrl]);
    return (
        <Center
            className={style({
                className
            })}
            testID="emtpy-state"
        >
            <VStack
                className="justify-center items-center w-full max-w-full lg:max-w-[400px]"
                space="2xl"
            >
                <VStack className="justify-center items-center" space="sm">
                    {imgUrl && (
                        <View
                            style={{
                                ...calculateDimension(imgWidth ?? 346, imgHeight, imgAspectRatio)
                            }}
                        >
                            <Image
                                alt="Empty image"
                                resizeMode="contain"
                                size="full"
                                source={source}
                            />
                        </View>
                    )}
                    {SVGComponent && (
                        <SVGComponent
                            {...calculateDimension(imgWidth ?? 346, imgHeight, imgAspectRatio)}
                        />
                    )}
                    <Text textAlign="center" variant="2xl">
                        {primaryText}
                    </Text>
                    {secondaryText &&
                        (typeof secondaryText === "string" ? (
                            <Text isParagraph textAlign="center" variant="md">
                                {secondaryText}
                            </Text>
                        ) : (
                            secondaryText
                        ))}
                </VStack>
                {children}
            </VStack>
        </Center>
    );
};

export default memo(EmptyState);
